import { useMemo } from 'react';
import { CalendarColumn, CalendarSchedule } from '../../../components/CustomDayCalendar/CustomDayCalendar';
import { DayOfWeekEnum, RoomSettings } from '../../../models';
import { parseTimeStringToMinutes } from '../../../shared/utils';
import { Translate } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { Presentation } from './homeTypes';

interface UseRoomBlockedPeriodsParams {
  columnType: Presentation;
  dates: Date[];
  rooms: Record<string, RoomSettings>;
  settings: { openningHour: string; closingHour: string };
  columns: CalendarColumn[];
  t: Translate<TK>;
  toWeekDay: (date: Date) => DayOfWeekEnum;
}

export const useRoomBlockedPeriods = ({
  columnType,
  dates,
  rooms,
  settings,
  columns,
  t,
  toWeekDay,
}: UseRoomBlockedPeriodsParams): CalendarSchedule[] => {
  const mutedColor = useMemo(() => {
    const color = getComputedStyle(document.documentElement).getPropertyValue('--text-muted').trim();
    return color || '#888888';
  }, []);

  return useMemo(() => {
    if (columnType !== 'room' && columnType !== 'professional') return [];

    const businessHoursStart = parseTimeStringToMinutes(settings.openningHour);
    const businessHoursEnd = parseTimeStringToMinutes(settings.closingHour);

    const buildBlockedForRoom = (
      roomName: string,
      availableSlots: { start: number; end: number }[],
      columnName: string,
      dayKey: string
    ): CalendarSchedule[] => {
      if (availableSlots.length === 0) {
        return [{
          id: `blocked-${columnName}-${dayKey}-${businessHoursStart}-${businessHoursEnd}`,
          columnName,
          color: mutedColor,
          opacity: 0.3,
          startMinutesOfDay: businessHoursStart,
          endMinutesOfDay: businessHoursEnd,
          content: `🚫 ${roomName}`,
          ignoreOverlap: true,
          toolTipContent: `${roomName} - ${t(TK.notAvailable)}`,
          onClick: () => {},
        }];
      }

      const blocked: CalendarSchedule[] = [];
      let currentTime = businessHoursStart;

      for (const slot of availableSlots) {
        if (currentTime < slot.start) {
          blocked.push({
            id: `blocked-${columnName}-${dayKey}-${currentTime}-${Math.min(slot.start, businessHoursEnd)}`,
            columnName,
            color: mutedColor,
            opacity: 0.3,
            startMinutesOfDay: currentTime,
            endMinutesOfDay: Math.min(slot.start, businessHoursEnd),
            content: `🚫 ${roomName}`,
            ignoreOverlap: true,
            toolTipContent: `${roomName} - ${t(TK.notAvailable)}`,
            onClick: () => {},
          });
        }
        currentTime = Math.max(currentTime, slot.end);
      }

      if (currentTime < businessHoursEnd) {
        blocked.push({
          id: `blocked-${columnName}-${dayKey}-${currentTime}-${businessHoursEnd}`,
          columnName,
          color: mutedColor,
          opacity: 0.3,
          startMinutesOfDay: currentTime,
          endMinutesOfDay: businessHoursEnd,
          content: `🚫 ${roomName}`,
          ignoreOverlap: true,
          toolTipContent: `${roomName} - ${t(TK.notAvailable)}`,
          onClick: () => {},
        });
      }

      return blocked;
    };

    const mergeIntervals = (intervals: { start: number; end: number }[]) => {
      const sorted = intervals.slice().sort((a, b) => a.start - b.start);
      const merged: { start: number; end: number }[] = [];

      for (const interval of sorted) {
        if (!merged.length || interval.start > merged[merged.length - 1].end) {
          merged.push({ ...interval });
        } else {
          merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, interval.end);
        }
      }
      return merged;
    };

    return dates.flatMap(date => {
      const dayKey = date.toISOString().substring(0, 10);
      const dayOfWeek = toWeekDay(date);

      const roomsAvailability = Object.entries(rooms).map(([roomName, roomSettings]) => {
        const availabilitySource = roomSettings.timeAvailability?.length ? roomSettings.timeAvailability : undefined;

        if (!availabilitySource) {
          return {
            roomName,
            availableSlots: [{
              start: businessHoursStart,
              end: businessHoursEnd,
            }],
          };
        }

        const roomAvailability = availabilitySource
          .slice()
          .sort((a, b) => new Date(b.from).getTime() - new Date(a.from).getTime())
          .find(a => new Date(a.from) <= date);

        const availableSlots = (roomAvailability?.timeSlots ?? [])
          .filter(slot => slot.dayOfWeek === dayOfWeek)
          .map(slot => ({
            start: parseTimeStringToMinutes(slot.start),
            end: parseTimeStringToMinutes(slot.end),
          }))
          .map(slot => ({
            start: Math.max(slot.start, businessHoursStart),
            end: Math.min(slot.end, businessHoursEnd),
          }))
          .filter(slot => slot.start < slot.end)
          .sort((a, b) => a.start - b.start);

        return { roomName, availableSlots };
      });

      if (columnType === 'room') {
        return roomsAvailability.flatMap(({ roomName, availableSlots }) =>
          buildBlockedForRoom(roomName, availableSlots, roomName, dayKey));
      }

      const mergedAvailability = mergeIntervals(
        roomsAvailability.flatMap(r => r.availableSlots).filter(a => a.start < a.end)
      );

      const fullyBlocked: { start: number; end: number }[] = [];
      let currentTime = businessHoursStart;

      for (const slot of mergedAvailability) {
        if (currentTime < slot.start) {
          fullyBlocked.push({ start: currentTime, end: slot.start });
        }
        currentTime = Math.max(currentTime, slot.end);
      }
      if (currentTime < businessHoursEnd) {
        fullyBlocked.push({ start: currentTime, end: businessHoursEnd });
      }

      return fullyBlocked.flatMap(blocked =>
        columns.map(col => ({
          id: `blocked-${col.name}-${dayKey}-${blocked.start}-${blocked.end}`,
          columnName: col.name,
          color: mutedColor,
          opacity: 0.3,
          startMinutesOfDay: blocked.start,
          endMinutesOfDay: blocked.end,
          content: `🚫 ${t(TK.rooms)}`,
          ignoreOverlap: true,
          toolTipContent: t(TK.notAvailable),
          onClick: () => {},
        }))
      );
    });
  }, [columnType, columns, dates, mutedColor, rooms, settings.closingHour, settings.openningHour, t, toWeekDay]);
};
